import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { CheckCircle2, LogIn, Mail } from "lucide-react";
import api from "../../const/axios";
import { signedIn } from "../../redux/actions";
import GoogleAuthButton from "@/components/Elements/GoogleAuthButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ErrorBanner, Field, OrDivider, PasswordInput, linkCls } from "./shared";

interface Props {
  onSignedIn: () => void;
  onLogin: () => void;
}

const RegisterForm: React.FC<Props> = ({ onSignedIn, onLogin }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors]           = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading]         = useState(false);
  const [doneText, setDoneText]       = useState<string | null>(null);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: "" }));
    setServerError("");
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim())                      errs.name     = t("auth.nameRequired");
    else if (form.name.trim().length < 2)       errs.name     = t("auth.nameMinLength");
    if (!form.email.trim())                     errs.email    = t("auth.emailRequired");
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email    = t("auth.emailInvalid");
    if (!form.password)                         errs.password = t("auth.passwordRequired");
    else if (form.password.length < 8)          errs.password = t("auth.passwordMinLength");
    if (!termsAccepted)                         errs.terms    = t("legal.termsRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fail = (err: any, fallback: string) =>
    setServerError(err?.response?.status === 429 ? t("auth.tooManyAttempts") : fallback);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    try {
      const res = await api.post("auth/register", { ...form, name: form.name.trim(), email: form.email.trim() });
      if (res.data.success) setDoneText(res.data.message ?? "");
      else setServerError(res.data.message);
    } catch (err) {
      fail(err, t("auth.operationError"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (authCode: string) => {
    setLoading(true);
    setServerError("");
    try {
      const res = await api.post("auth/google", { authCode });
      if (res.data.success && res.data.token) {
        dispatch(signedIn(res.data));
        onSignedIn();
        return;
      }
      setServerError(res.data.message);
    } catch (err) {
      fail(err, t("auth.googleAuthFailed"));
    }
    setLoading(false);
  };

  // Accounts start unconfirmed, so a successful sign-up ends at "check your email".
  if (doneText !== null) {
    return (
      <div className="text-center space-y-4 py-2">
        <div className="mx-auto w-14 h-14 rounded-full bg-green-50 dark:bg-green-900/60 border-2 border-green-200 dark:border-green-700 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-green-500" />
        </div>
        <div className="space-y-1">
          <p className="text-lg font-black text-gray-900 dark:text-gray-50">{t("auth.checkEmailTitle")}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("auth.checkEmailSubtitle")}</p>
        </div>
        {doneText && (
          <div className="flex items-start gap-2.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-900 rounded-xl px-3 py-2.5 text-left">
            <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500 dark:text-indigo-300" />
            <p className="text-sm text-blue-700 dark:text-blue-300">{doneText}</p>
          </div>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500">{t("auth.checkSpamNote")}</p>
        <Button className="w-full gap-2" onClick={onLogin}>
          <LogIn className="w-4 h-4" />
          {t("auth.goToLogin")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ErrorBanner message={serverError} />

      <GoogleAuthButton
        mode="signup"
        onSuccess={handleGoogleSuccess}
        onError={() => setServerError(t("auth.googleAuthFailed"))}
        loading={loading}
      />

      <OrDivider label={t("auth.orRegisterWith")} />

      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <Field error={errors.name}>
          <Input
            aria-label={t("auth.name")}
            placeholder={t("auth.name")}
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            autoFocus
          />
        </Field>
        <Field error={errors.email}>
          <Input
            type="email"
            aria-label={t("auth.email")}
            placeholder={t("auth.email")}
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
          />
        </Field>
        <Field error={errors.password}>
          <PasswordInput
            aria-label={t("auth.password")}
            placeholder={t("auth.password")}
            value={form.password}
            onChange={set("password") as any}
            autoComplete="new-password"
            minLength={8}
          />
        </Field>

        <div className="space-y-1">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setErrors(er => ({ ...er, terms: "" }));
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 accent-blue-500 cursor-pointer"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              <Trans
                i18nKey="legal.agreeToTerms"
                components={{
                  terms: <Link to="/terms" target="_blank" rel="noopener noreferrer" className={linkCls} />,
                  privacy: <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={linkCls} />,
                }}
              />
            </span>
          </label>
          {errors.terms && <p className="text-red-500 text-xs ml-6">{errors.terms}</p>}
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {t("auth.completeRegistration")}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
        {t("auth.alreadyHaveAccount")}{" "}
        <button type="button" onClick={onLogin} className={linkCls}>
          {t("auth.login")}
        </button>
      </p>
    </div>
  );
};

export default RegisterForm;
